import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, signal } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import {
  RegionUniverseSummary,
  UniverseService,
  UniverseSummary,
} from '../../../../woo_services.module/UniverseService';

@Component({
  selector: 'manage-universe',
  templateUrl: './manage-universe.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageUniverse {
  readonly acceptedFileTypes =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';

  @Input('headerPos') parentHeader: HTMLElement;
  targetUniverseTableData = signal<UniverseSummary[]>([]);
  regionUniverseTableData = signal<RegionUniverseSummary[]>([]);
  showUploadForm = signal<boolean>(false);
  newUniverseFile = null as File;

  readonly targetTableHeaders = ['Åldersspann', 'Kvinnor', 'Män', 'Alla'];
  readonly regionTableHeaders = ['Region', 'Universum'];
  fileName: string;
  loading = signal<boolean>(true);

  constructor(
    private modalService: ModalService,
    private universeService: UniverseService,
    private changeDetection: ChangeDetectorRef,
  ) {
    this.loadTargetUniverses();
    this.loadRegionUniverses();
  }

  get uploadDisabled(): boolean {
    return !this.newUniverseFile;
  }

  cancel(): void {
    this.showUploadForm.set(false);
    this.fileName = null;
    this.newUniverseFile = null;
  }

  sendFile(): void {
    const loaderModal = this.modalService.openLoader('Laddar upp nya universum...');
    this.universeService
      .uploadFile(this.newUniverseFile)
      .then(() => {
        this.loadTargetUniverses();
        this.loadRegionUniverses();
        this.showUploadForm.set(false);
      })
      .catch((err) => {
        this.modalService.openError(this.errorMsg(err.error_message));
      })
      .finally(() => loaderModal.close());
  }

  setFile(file: File): void {
    if (file && this.acceptedFileTypes.includes(file.type)) {
      this.newUniverseFile = file;
    } else {
      this.newUniverseFile = null as File;
    }
    this.fileName = file.name;
  }

  uploadFileLabel(): string {
    return !this.fileName ? 'Välj fil att ladda upp' : this.fileName;
  }

  private loadTargetUniverses() {
    this.universeService
      .getTargetUniverses()
      .then((universes) => {
        this.targetUniverseTableData.set(universes);
        this.changeDetection.markForCheck();
      })
      .catch(() => this.modalService.openError('Gick inte att importera Universumtalen för målgrupper'));
  }

  private loadRegionUniverses() {
    this.universeService
      .getRegionUniverses()
      .then((universes) => {
        this.regionUniverseTableData.set(universes);
        this.changeDetection.markForCheck();
      })
      .catch(() => this.modalService.openError('Gick inte att importera Universumtalen för regioner'));
  }

  private errorMsg(error: string) {
    const translations = {
      incorrect_header_in_file: `Filen saknar giltig kolumn. För målgruppssuniversum måste bladet innehålla "Target" och "UniSize", 
      respektive "Region" och "UniSize" på bladet för regionsuniversum`,

      un_expected_universe_size: 'Alla universum borde vara större än 0',
      un_expected_age_span_length: 'Alla åldersspannen borde vara 5 år',
      unable_to_parse_target_group: 'Det gick inte att läsa ut alla åldersgrupper',
      unable_to_parse_region: 'Ogiltig region i filen',

      missing_age_group_women: 'Det saknas åldersgrupp i filen för kvinnor. Förväntas [00-04 .. 95-99]',
      missing_age_group_men: 'Det saknas åldersgrupp i filen för män. Förväntas [00-04 .. 95-99]',

      missing_target_row: 'Det saknas rader för målgrupper',
      missing_region_row: 'Det saknas rader för regioner',
      missing_universes:
        'Det saknas universum - filen måste innehålla ett blad för målgruppsuniverum samt ett för regionsuniversum',

      universe_sizes_do_not_match: 'Totala universum för målgrupper respektive regioner överensstämmer inte',
      invalid_universe_cant_save: 'Någon av de nya universumen bryter mot reglerna. Inga nya universum är inlästa.',
    };

    return translations[error] !== undefined ? translations[error] : error;
  }
}
